import React from 'react';
import { Button, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import ModernAuthLayout from '../components/Auth/ModernAuthLayout';
import { BrandLogoIcon } from '../components/BrandLogo';
import { useGetSignupConfigQuery } from '../services/nestjs-base-api';

const { Text, Title } = Typography;

// Charter Cohort (SYR-106). What /charter serves once the intake has closed.
//
// It exists because brandscience.ai/charter is a static build with no knowledge
// of CHARTER_COHORT_ENABLED. When the flag goes off that page stays live with an
// "Apply for a place" button pointing here, and so do any emails and forwarded
// links. The previous behaviour was a silent redirect to sign-in, which left
// someone who had just read the whole offer staring at a login form.
//
// So this says the intake is closed, and sends them to whichever door is open.
const CharterClosedPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { data: signupConfig } = useGetSignupConfigQuery();

    // With Charter off, self-serve signup is usually back on, which makes it the
    // natural recovery. If it is not, there is nothing to offer but sign-in.
    const selfServeOpen = !!signupConfig?.publicSignupEnabled;

    const leftContent = (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '2rem' }}>
                <BrandLogoIcon size={48} />
                <span style={{ fontSize: '32px', fontWeight: '700' }}>
                    <span style={{ color: 'white' }}>BrandScience</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>.ai</span>
                </span>
            </div>
            <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', lineHeight: '1.2' }}>
                {intl.formatMessage({ id: 'charter.closed.heading' })}
            </h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.9, lineHeight: '1.6' }}>
                {intl.formatMessage({ id: 'charter.closed.description' })}
            </p>
        </div>
    );

    return (
        <ModernAuthLayout leftContent={leftContent}>
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    background: '#fef3c7',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.25rem',
                }}>
                    <CheckCircleOutlined style={{ fontSize: '30px', color: '#d97706' }} />
                </div>

                <Title level={3} style={{ marginBottom: '0.75rem' }}>
                    {intl.formatMessage({ id: 'charter.closed.title' })}
                </Title>
                <Text type="secondary" style={{ fontSize: 15, display: 'block', marginBottom: '1.75rem' }}>
                    {intl.formatMessage({
                        id: selfServeOpen ? 'charter.closed.bodyWithSignup' : 'charter.closed.body',
                    })}
                </Text>

                {selfServeOpen && (
                    <Button
                        type="primary"
                        block
                        size="large"
                        onClick={() => navigate('/sign-up')}
                        style={{ height: '48px', borderRadius: '12px', fontSize: '16px', fontWeight: 600, marginBottom: '0.75rem' }}
                    >
                        {intl.formatMessage({ id: 'charter.closed.signupCta' })}
                    </Button>
                )}

                <Text type="secondary" style={{ fontSize: 13 }}>
                    {intl.formatMessage({ id: 'charter.confirmation.existingAccount' })}{' '}
                    <Link to="/sign-in">{intl.formatMessage({ id: 'charter.confirmation.signIn' })}</Link>
                </Text>
            </div>
        </ModernAuthLayout>
    );
};

export default CharterClosedPage;
